import { Directive, EventEmitter, Input, Output } from '@angular/core';
import { ListFilterState } from 'src/app/shared/components/list-filter-toolbar/list-filter-toolbar.component';
import { applyListFilter } from 'src/app/shared/helpers/listOwnershipFilter.helper';

export interface ViewLookup {
    id: string;
    name: string;
    isShared?: boolean;
    canManage?: boolean;
    createdByUserId?: string;
}

@Directive()
export abstract class ViewSelectorBase<T extends ViewLookup, TAdd = null> {
    @Input() defaultViewId?: string;
    @Input() selectedViewId?: string;
    @Output() viewSelected = new EventEmitter<{ view: T; firstRender: boolean }>();
    @Output() viewsChanged = new EventEmitter<T[]>();
    @Output() addView = new EventEmitter<TAdd | null>();
    @Input() orgDefaultViewId?: string;
    @Input() showUseMyDefault = false;
    @Input() useMyDefault = false;
    @Input() canToggleUseMyDefault = false;
    @Output() useMyDefaultChange = new EventEmitter<boolean>();

    views: T[] = [];
    selectedView?: T | null = null;
    filteredViews: T[] = [];
    isLoading = false;
    isOpen = false;
    filterState: ListFilterState = { search: '', hideShared: false };
    filterResetTick = 0;
    renamingViewId?: string | null = null;
    renameInput = '';
    menuView: T | null = null;
    menuOpened = false;

    isTemporaryView = false;
    currentViewName?: string;
    canSaveView = true;
    cannotSaveViewTooltip?: string;
    addViewLabel = 'Add View';

    protected abstract get currentUserId(): string;

    abstract fetchViews(): void;
    abstract confirmRename(view: T): void | Promise<void>;
    abstract duplicateView(view: T): void | Promise<void>;
    abstract deleteView(view: T): void;
    abstract makeDefaultView(view: T): void;

    sortViews(): void {
        if (!this.views) return;
        const rank = (view: T): number => {
            if (view.id === this.defaultViewId) return 0;
            if (this.orgDefaultViewId && view.id === this.orgDefaultViewId) return 1;
            return view.isShared ? 3 : 2;
        };
        this.views.sort((a, b) => rank(a) - rank(b) || a.name.localeCompare(b.name));
        const targetView = this.views.find(v => v.id === (this.selectedViewId || this.defaultViewId));
        if (targetView) this.selectedView = targetView;
        this.applyViewFilter();
        if (targetView && !this.selectedViewId && this.defaultViewId) {
            this.viewSelected.emit({ view: targetView, firstRender: true });
        }
    }

    syncSelectedView(): void {
        if (!this.views || !this.selectedViewId) return;
        const targetView = this.views.find(v => v.id === this.selectedViewId);
        if (targetView && this.selectedView?.id !== targetView.id) {
            this.selectedView = targetView;
        }
    }

    selectView(view: T, firstRender = false): void {
        if (this.renamingViewId) return;
        this.closeMenu();
        this.selectedView = view;
        this.viewSelected.emit({ view, firstRender });
        this.isOpen = false;
    }

    triggerAddView(): void {
        this.isOpen = false;
        this.addView.emit(null);
    }

    startRenaming(view: T): void {
        if (view.canManage === false) return;
        this.renamingViewId = view.id;
        this.renameInput = view.name;
        this.menuOpened = false;
        this.menuView = null;
    }

    cancelRenaming(): void {
        this.renamingViewId = undefined;
        this.renameInput = '';
    }

    onFilterChange(state: ListFilterState): void {
        this.filterState = state;
        this.applyViewFilter();
    }

    protected applyViewFilter(): void {
        this.filteredViews = applyListFilter(this.views, this.filterState, this.currentUserId, {
            name: view => view.name,
            createdByUserId: view => view.createdByUserId
        });
    }

    protected resetViewFilter(): void {
        this.filterState = { search: '', hideShared: false };
        this.filterResetTick++;
        this.applyViewFilter();
    }

    onOutsideClick(): void {
        if (!this.isOpen || this.renamingViewId) return;
        if (this.menuOpened) {
            this.closeMenu();
            return;
        }
        this.isOpen = false;
    }

    toggleDropdown(): void {
        this.isOpen = !this.isOpen;
        if (this.isOpen) this.resetViewFilter();
        else this.closeMenu();
    }

    openMenuFor(view: T, event: MouseEvent): void {
        event.stopPropagation();
        this.menuOpened = true;
        this.menuView = view;
    }

    closeMenu(): void {
        this.menuOpened = false;
        this.menuView = null;
    }

    isDefault(view: T): boolean {
        return view.id === this.defaultViewId;
    }

    trackByViewId(_: number, view: T): string {
        return view.id;
    }
}
